import React, { useState, useEffect } from 'react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import AnimatedSection from '../../common/AnimatedSection';
import { getUserProfile } from '../../../utils/userStorage';
import { validateContactForm } from '../../../utils/validation';
import { buildEmergencyPayload } from '../../../utils/emergencyPayloadBuilder';
import { sendEmergencyRequest } from '../../../services/emergencyService';
import { ExclamationTriangleIcon, CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';

export default function StatsSection() {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    sujet: '',
    message: '',
  });

  const [previousStepData, setPreviousStepData] = useState(null);
  const [hasPrefilled, setHasPrefilled] = useState(false);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [apiError, setApiError] = useState(null);

  // Récupération automatique des données saisies aux étapes précédentes (ex: Recensement)
  useEffect(() => {
    const profile = getUserProfile();
    if (profile) {
      setPreviousStepData(profile);
      const computedNom = profile.prenom && profile.nom
        ? `${profile.prenom} ${profile.nom}`
        : profile.nom || '';

      setFormData((prev) => ({
        ...prev,
        nom: prev.nom || computedNom,
        telephone: prev.telephone || profile.telephone || '',
      }));
      setHasPrefilled(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (apiError) {
      setApiError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Validation locale des données
    const validation = validateContactForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    setLoading(true);
    setValidationErrors({});
    setApiError(null);

    // 2. Construction du payload enrichi avec le parcours utilisateur
    const payload = buildEmergencyPayload(formData, previousStepData);

    // 3. Appel sécurisé au service / fonction d'urgence
    const response = await sendEmergencyRequest(payload);

    setLoading(false);

    if (response.success) {
      setIsSubmitted(true);
      setFormData({
        nom: previousStepData ? (previousStepData.prenom ? `${previousStepData.prenom} ${previousStepData.nom}` : previousStepData.nom) : '',
        email: '',
        telephone: previousStepData?.telephone || '',
        sujet: '',
        message: '',
      });
    } else {
      setApiError(response.error || "Une erreur est survenue lors de la transmission du message.");
    }
  };

  return (
    <AnimatedSection animation="fade-up" id="contact" className="max-w-4xl mx-auto my-16 px-4">
      <div className="text-center mb-8 space-y-2">
        <h2 className="font-title text-2xl sm:text-3xl md:text-4xl font-extrabold text-acem-primary-dark">
          Contactez l'ACEM Fès
        </h2>
        <p className="text-sm sm:text-base text-acem-muted max-w-xl mx-auto">
          Une question, une demande d'information ou un besoin d'accompagnement ? Envoyez-nous un message et notre équipe vous répondra rapidement.
        </p>
      </div>

      <Card variant="elevated" className="p-6 sm:p-10 relative overflow-hidden shadow-acem-lg">
        {/* Glow backdrop */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-acem-primary/10 rounded-full filter blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-acem-accent/10 rounded-full filter blur-3xl pointer-events-none" />

        {/* Indication de données récupérées des étapes précédentes */}
        {hasPrefilled && !isSubmitted && (
          <div className="mb-6 p-3.5 rounded-xl bg-emerald-50/90 border border-emerald-200 text-emerald-900 text-xs sm:text-sm font-medium flex items-center gap-2.5 shadow-sm animate-fade-in relative z-10">
            <SparklesIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <span>
              Vos informations d'inscription (recensement) ont été automatiquement récupérées du parcours précédent.
            </span>
          </div>
        )}

        {isSubmitted ? (
          <div className="py-10 text-center space-y-4 animate-fade-in relative z-10">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-2xl font-bold shadow-sm">
              <CheckCircleIcon className="w-10 h-10" />
            </div>
            <h3 className="font-title text-xl font-bold text-acem-primary-dark">
              Message envoyé avec succès !
            </h3>
            <p className="text-sm text-acem-muted max-w-md mx-auto leading-relaxed">
              Merci de nous avoir contactés. Le bureau de l'ACEM Fès a bien reçu votre demande et vous recontactera rapidement.
            </p>
            <div className="pt-2">
              <Button variant="secondary" size="md" onClick={() => setIsSubmitted(false)}>
                Envoyer un autre message
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
            {/* Message d'erreur API si l'appel échoue */}
            {apiError && (
              <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs sm:text-sm font-medium flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="font-bold">Échec de l'envoi :</strong> {apiError}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Nom complet */}
              <div className="space-y-1.5">
                <label htmlFor="nom" className="block text-xs sm:text-sm font-semibold text-acem-text">
                  Nom complet <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  required
                  value={formData.nom}
                  onChange={handleChange}
                  placeholder="Votre nom et prénom"
                  className={`w-full px-4 py-2.5 rounded-xl border bg-white text-acem-text text-sm focus:outline-none focus:ring-2 transition-all ${validationErrors.nom
                    ? 'border-rose-400 focus:ring-rose-500'
                    : 'border-acem-border focus:ring-acem-primary focus:border-transparent'
                    }`}
                />
                {validationErrors.nom && (
                  <p className="text-xs text-rose-600 font-medium">{validationErrors.nom}</p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-acem-text">
                  Adresse email <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre.email@example.com"
                  className={`w-full px-4 py-2.5 rounded-xl border bg-white text-acem-text text-sm focus:outline-none focus:ring-2 transition-all ${validationErrors.email
                    ? 'border-rose-400 focus:ring-rose-500'
                    : 'border-acem-border focus:ring-acem-primary focus:border-transparent'
                    }`}
                />
                {validationErrors.email && (
                  <p className="text-xs text-rose-600 font-medium">{validationErrors.email}</p>
                )}
              </div>
            </div>

            {/* Téléphone */}
            <div className="space-y-1.5">
              <label htmlFor="telephone" className="block text-xs sm:text-sm font-semibold text-acem-text">
                Numéro Téléphone / WhatsApp <span className="text-acem-muted font-normal">(Optionnel)</span>
              </label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                placeholder="Ex: +212 6XX XXXXXX"
                className={`w-full px-4 py-2.5 rounded-xl border bg-white text-acem-text text-sm focus:outline-none focus:ring-2 transition-all ${validationErrors.telephone
                    ? 'border-rose-400 focus:ring-rose-500'
                    : 'border-acem-border focus:ring-acem-primary focus:border-transparent'
                  }`}
              />
              {validationErrors.telephone && (
                <p className="text-xs text-rose-600 font-medium">{validationErrors.telephone}</p>
              )}
            </div>

            {/* Objet */}
            <div className="space-y-1.5">
              <label htmlFor="sujet" className="block text-xs sm:text-sm font-semibold text-acem-text">
                Objet / Sujet <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                id="sujet"
                name="sujet"
                required
                value={formData.sujet}
                onChange={handleChange}
                placeholder="Ex: Renseignement inscription, partenariat, assistance..."
                className={`w-full px-4 py-2.5 rounded-xl border bg-white text-acem-text text-sm focus:outline-none focus:ring-2 transition-all ${validationErrors.sujet
                  ? 'border-rose-400 focus:ring-rose-500'
                  : 'border-acem-border focus:ring-acem-primary focus:border-transparent'
                  }`}
              />
              {validationErrors.sujet && (
                <p className="text-xs text-rose-600 font-medium">{validationErrors.sujet}</p>
              )}
            </div>

            {/* Message */}
            <div className="space-y-1.5">
              <label htmlFor="message" className="block text-xs sm:text-sm font-semibold text-acem-text">
                Votre message <span className="text-rose-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows="4"
                value={formData.message}
                onChange={handleChange}
                placeholder="Rédigez votre message ici..."
                className={`w-full px-4 py-2.5 rounded-xl border bg-white text-acem-text text-sm focus:outline-none focus:ring-2 transition-all resize-y min-h-[110px] ${validationErrors.message
                  ? 'border-rose-400 focus:ring-rose-500'
                  : 'border-acem-border focus:ring-acem-primary focus:border-transparent'
                  }`}
              />
              {validationErrors.message && (
                <p className="text-xs text-rose-600 font-medium">{validationErrors.message}</p>
              )}
            </div>

            {/* Bouton de soumission avec protection anti-double clic */}
            <div className="pt-2 text-center sm:text-right">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={loading}
                className="w-full sm:w-auto min-w-[180px]"
              >
                {loading ? 'Envoi en cours...' : 'Envoyer le message'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </AnimatedSection>
  );
}

